import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Sun, Building, Lightbulb, Droplet, Wrench } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <Sun className="w-16 h-16 text-[#FFC107]" />,
      title: 'Residential Solar Rooftop Systems',
      description: 'Transform your home with our premium residential solar solutions. We design and install custom solar rooftop systems that significantly reduce your electricity bills while increasing your property value. Our residential packages include high-efficiency solar panels, inverters, mounting structures, and complete installation services.',
      features: [
        'Custom system design for your home',
        'High-efficiency solar panels',
        'Net metering support',
        'Government subsidy assistance',
        '25-year performance warranty'
      ],
      image: 'https://images.pexels.com/photos/29206495/pexels-photo-29206495.jpeg'
    },
    {
      icon: <Building className="w-16 h-16 text-[#FFC107]" />,
      title: 'Commercial & Industrial Solar Solutions',
      description: 'Power your business with clean energy and reduce operational costs. Our commercial and industrial solar solutions are designed for large-scale energy requirements. From factories to office complexes, we deliver reliable solar power systems that maximize ROI and demonstrate your commitment to sustainability.',
      features: [
        'Large-scale installations',
        'Energy audit and optimization',
        'Tax benefits and depreciation',
        'Minimal business disruption',
        'Scalable power solutions'
      ],
      image: 'https://images.pexels.com/photos/9875408/pexels-photo-9875408.jpeg'
    },
    {
      icon: <Lightbulb className="w-16 h-16 text-[#FFC107]" />,
      title: 'Solar Street Lights',
      description: 'Illuminate your streets, parks, and public spaces with eco-friendly solar street lights. Our solar lighting solutions are perfect for municipalities, residential societies, and industrial complexes. These self-sustaining lights require no external power and minimal maintenance.',
      features: [
        'Zero electricity costs',
        'Automatic dusk-to-dawn operation',
        'Weather-resistant design',
        'Easy installation'
      ],
      image: 'https://images.unsplash.com/photo-1624397640148-949b1732bb0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHJvb2Z0b3AlMjBwYW5lbHN8ZW58MHx8fHwxNzYwNDYxMjc2fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Droplet className="w-16 h-16 text-[#FFC107]" />,
      title: 'Solar Water Heaters',
      description: 'Enjoy hot water 365 days a year while saving on electricity. Our solar water heating systems are highly efficient and eco-friendly, perfect for homes, hotels, hospitals, and industries. Experience consistent hot water supply powered by the sun.',
      features: [
        'Instant hot water supply',
        'Reduces electricity bills by 80%',
        'Suitable for all climates',
        'Low maintenance'
      ],
      image: 'https://images.unsplash.com/photo-1668097613572-40b7c11c8727?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxzb2xhciUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3NjA0NjEyODJ8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Wrench className="w-16 h-16 text-[#FFC107]" />,
      title: 'Maintenance & AMC Services',
      description: 'Keep your solar systems running at peak efficiency with our comprehensive maintenance and Annual Maintenance Contract (AMC) services. Regular maintenance ensures maximum power generation and extends the life of your solar installation.',
      features: [
        'Regular system inspection',
        'Panel cleaning services',
        'Performance monitoring',
        'Quick repair response',
        'Preventive maintenance'
      ],
      image: 'https://images.unsplash.com/photo-1542336391-ae2936d8efe4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxzb2xhciUyMGluc3RhbGxhdGlvbnxlbnwwfHx8fDE3NjA0NjEyODJ8MA&ixlib=rb-4.1.0&q=85'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#0D47A1] to-[#1565C0] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">Our Services</h1>
          <p className="text-xl text-gray-100 leading-relaxed">
            Comprehensive solar solutions for every need — from residential to industrial
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto space-y-16">
          {services.map((service, index) => (
            <Card 
              key={index} 
              className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-none bg-gray-50"
            >
              <CardContent className="p-0">
                <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                  <div className={`p-8 md:p-12 flex flex-col justify-center ${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                    <div className="flex items-center gap-4 mb-6">
                      {service.icon}
                      <h2 className="text-3xl font-bold text-[#0D47A1]">{service.title}</h2>
                    </div>
                    <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                      {service.description}
                    </p>
                    <div className="mb-6">
                      <h3 className="font-bold text-[#4CAF50] mb-3 text-lg">Key Features:</h3>
                      <ul className="space-y-2">
                        {service.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start">
                            <span className="text-[#4CAF50] mr-2">✓</span>
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link to="/contact">
                      <Button className="bg-[#FFC107] hover:bg-[#FFD54F] text-black font-semibold transition-all duration-300 hover:shadow-lg">
                        Learn More
                      </Button>
                    </Link>
                  </div>
                  <div className={`h-full min-h-[400px] ${index % 2 === 1 ? 'md:col-start-1 md:row-start-1' : ''}`}>
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#4CAF50] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Solar Journey?</h2>
          <p className="text-xl mb-8 text-gray-100">
            Contact us today for a free consultation and customized solar solution
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-white hover:bg-gray-100 text-[#4CAF50] font-semibold text-lg px-8 py-6 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              Get Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;